"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { motion, MotionConfig } from "framer-motion";
import {
  Activity,
  ArrowUp,
  BookOpen,
  FileDown,
  Gavel,
  History,
  HandCoins,
  Link2,
  ListChecks,
  Printer,
  RefreshCw,
  Satellite,
  TriangleAlert,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/hooks/use-toast";
import {
  KpiGrid,
  LoopStepper,
  OpsCard,
  RealityBanner,
  SessionActivityCard,
  StrategyCard,
  WalletsCard,
  timeAgo,
} from "@/components/mission/sections";
import { FlagshipCard } from "@/components/mission/flagship";
import { PaidApiQuickstartCard } from "@/components/mission/paid-api-card";
import { WakeCadenceCard } from "@/components/mission/wake-cadence";
import { HumTractionCard } from "@/components/mission/hum-traction";
import { CelebrationGate } from "@/components/mission/celebration";
import { PrintReport } from "@/components/mission/print-report";
import { downloadReport } from "@/components/mission/report";
import { CommandPalette, PaletteTrigger, TAB_ORDER, type TabValue } from "@/components/mission/command-palette";
import {
  DecisionsView,
  EarningsView,
  EvidenceView,
  NextActionsView,
  PipelineView,
  ResearchView,
  TimelineView,
  type TimelineItem,
} from "@/components/mission/tables";
import type { MissionData } from "@/types/mission";

const REFRESH_SECONDS = 30;

export function MissionDashboard() {
  const { toast } = useToast();
  const [data, setData] = useState<MissionData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [countdown, setCountdown] = useState(REFRESH_SECONDS);
  const [now, setNow] = useState(() => Date.now());
  const [tab, setTab] = useState<TabValue>("pipeline");
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const mounted = useRef(true);
  const lastSyncRef = useRef(0);
  const tabsRef = useRef<HTMLDivElement | null>(null);

  /* tab selection always flows through here so the URL hash (#timeline, …)
     stays in sync no matter how the view was picked — click, palette,
     keyboard, timeline jump, or a pasted deep link */
  const setTabWithHash = useCallback((v: TabValue) => {
    setTab(v);
    window.history.replaceState(null, "", `#${v}`);
  }, []);

  const load = useCallback(async () => {
    try {
      const res = await fetch("/api/mission", { cache: "no-store" });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        throw new Error(body.error ?? `HTTP ${res.status}`);
      }
      const json = (await res.json()) as MissionData;
      if (mounted.current) {
        setData(json);
        setError(null);
        setCountdown(REFRESH_SECONDS);
        lastSyncRef.current = Date.now();
      }
    } catch (e) {
      if (mounted.current) setError(e instanceof Error ? e.message : String(e));
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    load();
    const poll = window.setInterval(() => {
      // smart pause: never poll while the tab is hidden (owner is not looking)
      if (!document.hidden) load();
    }, REFRESH_SECONDS * 1000);
    return () => {
      mounted.current = false;
      window.clearInterval(poll);
    };
  }, [load]);

  /* one shared 1s clock drives: countdown, "synced Xs ago", mission-day badge */
  useEffect(() => {
    const tick = window.setInterval(() => {
      if (document.hidden) return; // freeze countdown while hidden
      setNow(Date.now());
      setCountdown((c) => (c <= 1 ? REFRESH_SECONDS : c - 1));
    }, 1000);
    return () => window.clearInterval(tick);
  }, []);

  /* catch-up: on return to a long-hidden tab, sync immediately if stale >10s */
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && Date.now() - lastSyncRef.current > 10_000) {
        load();
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [load]);

  /* keyboard shortcuts: 1..7 tabs · r refresh · k / Ctrl+K palette */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const typing =
        t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable);
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((p) => !p);
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (typing) return;
      const num = Number(e.key);
      if (num >= 1 && num <= TAB_ORDER.length) {
        setTabWithHash(TAB_ORDER[num - 1]);
        tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        return;
      }
      if (e.key.toLowerCase() === "r") {
        load();
        return;
      }
      if (e.key.toLowerCase() === "p") {
        window.print();
        return;
      }
      if (e.key.toLowerCase() === "k") {
        setPaletteOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [load, setTabWithHash]);

  /* scroll-to-top affordance after the first screen */
  useEffect(() => {
    const onScroll = () => setShowScrollTop(window.scrollY > 700);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* URL hash deep-links (#timeline, #decisions, …): shareable views that
     survive refreshes. replaceState keeps browser history clean (back exits
     the dashboard instead of cycling tabs); hashchange still covers manual
     hash edits and pasted links while the page is open. */
  const applyHash = useCallback((hash: string): boolean => {
    const v = hash.replace(/^#/, "");
    if ((TAB_ORDER as readonly string[]).includes(v)) {
      setTab(v as TabValue);
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!applyHash(window.location.hash)) return;
    // a shared link should land on the requested view once data is in
    const t = window.setTimeout(
      () => tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
      400,
    );
    return () => window.clearTimeout(t);
  }, [applyHash]);

  useEffect(() => {
    const onHash = (e: HashChangeEvent) => applyHash(new URL(e.newURL).hash);
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, [applyHash]);

  const phase = data?.state.phase ?? "…";
  const missionDay = data
    ? Math.max(1, Math.floor((now - new Date(data.state.started_at).getTime()) / 86_400_000) + 1)
    : null;
  const syncedAgo = data ? timeAgo(data.generated_at) : "…";
  const navigate = useCallback(
    (v: TabValue) => {
      setTabWithHash(v);
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    },
    [setTabWithHash],
  );

  /* timeline click-through: jump to the item's record in its source view,
     scroll it into view and flash it (research items deep-open the doc dialog) */
  const openTimelineItem = useCallback(
    (item: TimelineItem) => {
      const tabFor: Record<TimelineItem["kind"], TabValue> = {
        action: "evidence",
        decision: "decisions",
        research: "research",
        earning: "earnings",
      };
      setTabWithHash(tabFor[item.kind]);
      tabsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      window.setTimeout(() => {
        if (item.kind === "research") {
          window.dispatchEvent(new CustomEvent("mission:open-research", { detail: { filename: item.id } }));
        } else {
          window.dispatchEvent(new CustomEvent("mission:focus-item", { detail: { id: item.id } }));
        }
      }, 160);
    },
    [setTabWithHash],
  );

  const copyViewLink = useCallback(() => {
    const url = `${window.location.origin}${window.location.pathname}#${tab}`;
    navigator.clipboard
      .writeText(url)
      .then(() =>
        toast({
          title: "View link copied",
          description: url,
        }),
      )
      .catch(() => toast({ title: "Copy failed", description: "Clipboard unavailable in this context" }));
  }, [tab, toast]);

  return (
    <MotionConfig reducedMotion="user">
      <div className="relative flex min-h-screen flex-col bg-zinc-950 text-zinc-100 print:block print:min-h-0 print:bg-white print:text-zinc-900">
        {/* background texture */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-x-0 top-0 h-72 bg-[radial-gradient(ellipse_at_top,rgba(16,185,129,0.08),transparent_65%)] print:hidden"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,rgba(63,63,70,0.15)_1px,transparent_1px),linear-gradient(to_bottom,rgba(63,63,70,0.15)_1px,transparent_1px)] bg-[size:36px_36px] [mask-image:radial-gradient(ellipse_at_center,black_30%,transparent_75%)] print:hidden"
        />

        {/* header */}
        <header className="sticky top-0 z-40 border-b border-zinc-800/60 bg-zinc-950/85 backdrop-blur supports-[backdrop-filter]:bg-zinc-950/70 print:hidden">
          <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-2 px-4 sm:px-6 lg:px-8">
            <div className="flex min-w-0 items-center gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg border border-emerald-500/30 bg-emerald-500/10">
                <Satellite className="size-4.5 text-emerald-400" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h1 className="truncate font-mono text-sm font-bold tracking-[0.18em] text-zinc-100">
                  MISSION CONTROL
                </h1>
                <p className="truncate text-[11px] text-zinc-500">
                  Autonomous Crypto Earning System
                  {missionDay !== null && (
                    <span className="ml-1.5 font-mono text-zinc-600">· day {missionDay}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1.5 sm:gap-2 lg:gap-3">
              <PaletteTrigger onClick={() => setPaletteOpen(true)} />
              <span className="hidden items-center gap-1.5 rounded-md border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 font-mono text-[10px] font-semibold tracking-wider text-emerald-400 sm:inline-flex">
                <span className="relative flex size-1.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
                  <span className="relative inline-flex size-1.5 rounded-full bg-emerald-400" />
                </span>
                OPERATIONAL
              </span>
              <span className="hidden rounded-md border border-zinc-700/60 bg-zinc-800/30 px-2 py-1 font-mono text-[10px] font-semibold tracking-wider text-zinc-400 md:inline-flex">
                {phase}
              </span>
              <span
                className="font-mono text-[10px] tabular-nums text-zinc-600"
                title="Data freshness + auto-refresh countdown"
              >
                synced {syncedAgo} · refresh in {countdown}s
              </span>
              <Button
                variant="outline"
                size="icon"
                className="hidden size-8 border-zinc-700/60 bg-zinc-800/30 text-zinc-400 hover:text-zinc-100 sm:inline-flex"
                onClick={() => {
                  if (!data) return;
                  downloadReport(data);
                  toast({ title: "Mission report downloaded", description: "Truthful markdown export of the live mission state" });
                }}
                disabled={!data}
                aria-label="Download truthful mission report as markdown"
                title="Download mission report (.md) — truthful export"
              >
                <FileDown className="size-3.5" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="hidden size-8 border-zinc-700/60 bg-zinc-800/30 text-zinc-400 hover:text-zinc-100 sm:inline-flex"
                onClick={() => window.print()}
                aria-label="Print mission report"
                title="Print mission report (p)"
              >
                <Printer className="size-3.5" aria-hidden="true" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-8 border-zinc-700/60 bg-zinc-800/30 text-zinc-400 hover:text-zinc-100"
                onClick={load}
                disabled={loading}
                aria-label="Refresh mission state now"
              >
                <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
          {/* auto-refresh progress hairline — depletes over the polling window */}
          <div className="h-px w-full bg-zinc-800/40" aria-hidden="true">
            <div
              className="h-full bg-emerald-500/60 transition-[width] duration-1000 ease-linear motion-reduce:transition-none"
              style={{ width: loading ? "100%" : `${(countdown / REFRESH_SECONDS) * 100}%` }}
            />
          </div>
        </header>

        {/* main */}
        <main className="relative z-10 mx-auto w-full max-w-7xl flex-1 space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:px-8 print:hidden">
          {error && (
            <div
              role="alert"
              className="flex items-center gap-3 rounded-lg border border-rose-500/30 bg-rose-500/5 px-4 py-3 text-sm text-rose-300"
            >
              <TriangleAlert className="size-5 shrink-0" aria-hidden="true" />
              <div className="min-w-0">
                <p className="font-semibold">Could not load mission state</p>
                <p className="truncate text-xs text-rose-400/80">{error}</p>
              </div>
            </div>
          )}

          {!data && loading && !error ? (
            <div className="space-y-6">
              <Skeleton className="h-16 w-full rounded-lg bg-zinc-900/60" />
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-28 rounded-lg bg-zinc-900/60" />
                ))}
              </div>
              <Skeleton className="h-40 w-full rounded-lg bg-zinc-900/60" />
            </div>
          ) : data ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4 }}
              className="space-y-6"
            >
              {/* celebration gate — renders nothing until earnings.json gains verified entries */}
              <CelebrationGate data={data} />
              <RealityBanner data={data} />
              <KpiGrid data={data} />

              {data.flagship && <FlagshipCard flagship={data.flagship} />}

              {/* public quickstart for the paid x402 APIs (curl + pay flow + catalog links) */}
              <PaidApiQuickstartCard />

              {/* publishing lane traction (OPP-043): live hum.pub platform + article counters */}
              <HumTractionCard />

              <div className="grid min-w-0 gap-4 lg:grid-cols-3 lg:gap-6">
                <div className="min-w-0 lg:col-span-2">
                  <StrategyCard state={data.state} />
                </div>
                <div className="min-w-0">
                  <WalletsCard state={data.state} monitoring={data.monitoring} />
                </div>
              </div>

              <div className="grid min-w-0 gap-4 lg:grid-cols-3 lg:gap-6">
                <div className="min-w-0 lg:col-span-2">
                  <LoopStepper phase={data.state.phase} />
                </div>
                <div className="min-w-0">
                  <SessionActivityCard data={data} />
                </div>
              </div>

              <OpsCard state={data.state} radar={data.ste_radar} dealwork={data.dealwork_radar} />

              {/* autonomous scheduling layer — roles, cadence, live next-fire countdowns */}
              <WakeCadenceCard data={data} />

              <div ref={tabsRef} className="scroll-mt-24">
                <Tabs value={tab} onValueChange={(v) => setTabWithHash(v as TabValue)} className="space-y-4">
                  <div className="flex items-center gap-2">
                    <TabsList className="tabs-fade h-10 min-w-0 flex-1 justify-start gap-1 overflow-x-auto rounded-lg border border-zinc-800/70 bg-zinc-900/40 p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                    <TabsTrigger
                      value="pipeline"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 motion-reduce:transition-none"
                    >
                      <Activity className="size-3.5" aria-hidden="true" />
                      Pipeline
                      <span className="font-mono text-[10px] text-zinc-500">{data.pipeline.length}</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="earnings"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 motion-reduce:transition-none"
                    >
                      <HandCoins className="size-3.5" aria-hidden="true" />
                      Earnings
                      <span className="font-mono text-[10px] text-zinc-500">{data.earnings.length}</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="timeline"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 motion-reduce:transition-none"
                    >
                      <History className="size-3.5" aria-hidden="true" />
                      Timeline
                      <span className="font-mono text-[10px] text-zinc-500">
                        {data.actions.length + data.decisions.length + data.research.length + data.earnings.length}
                      </span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="evidence"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 motion-reduce:transition-none"
                    >
                      <ListChecks className="size-3.5" aria-hidden="true" />
                      Evidence Log
                      <span className="font-mono text-[10px] text-zinc-500">{data.actions.length}</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="next"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 data-[state=active]:bg-emerald-500/15 data-[state=active]:text-emerald-300 motion-reduce:transition-none"
                    >
                      Next Actions
                      <span className="font-mono text-[10px] text-zinc-500">{data.next_actions.length}</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="decisions"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-500/60 data-[state=active]:bg-violet-500/15 data-[state=active]:text-violet-300 motion-reduce:transition-none"
                    >
                      <Gavel className="size-3.5" aria-hidden="true" />
                      Decisions
                      <span className="font-mono text-[10px] text-zinc-500">{data.decisions.length}</span>
                    </TabsTrigger>
                    <TabsTrigger
                      value="research"
                      className="gap-2 rounded-md font-mono text-xs transition-all duration-150 hover:bg-zinc-800/60 hover:text-zinc-200 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-violet-500/60 data-[state=active]:bg-violet-500/15 data-[state=active]:text-violet-300 motion-reduce:transition-none"
                    >
                      <BookOpen className="size-3.5" aria-hidden="true" />
                      Research
                      <span className="font-mono text-[10px] text-zinc-500">{data.research.length}</span>
                    </TabsTrigger>
                    </TabsList>
                    {/* permlink — copies a URL that reopens this exact view */}
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={copyViewLink}
                      aria-label="Copy link to this view"
                      title="Copy a link that reopens this view"
                      className="size-9 shrink-0 border-zinc-800/70 bg-zinc-900/40 text-zinc-500 hover:border-emerald-500/30 hover:text-emerald-300"
                    >
                      <Link2 className="size-3.5" aria-hidden="true" />
                    </Button>
                  </div>

                  <TabsContent value="pipeline">
                    <PipelineView pipeline={data.pipeline} />
                  </TabsContent>
                  <TabsContent value="earnings">
                    <EarningsView earnings={data.earnings} />
                  </TabsContent>
                  <TabsContent value="timeline">
                    <TimelineView data={data} onOpen={openTimelineItem} />
                  </TabsContent>
                  <TabsContent value="evidence">
                    <EvidenceView actions={data.actions} />
                  </TabsContent>
                  <TabsContent value="next">
                    <NextActionsView queue={data.next_actions} />
                  </TabsContent>
                  <TabsContent value="decisions">
                    <DecisionsView decisions={data.decisions} />
                  </TabsContent>
                  <TabsContent value="research">
                    <ResearchView research={data.research} />
                  </TabsContent>
                </Tabs>
              </div>
            </motion.div>
          ) : null}
        </main>

        {/* sticky footer */}
        <footer className="relative z-10 mt-auto border-t border-zinc-800/60 bg-zinc-950/90 pb-[env(safe-area-inset-bottom)] backdrop-blur print:hidden">
          <div className="mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-1.5 px-4 py-4 text-[11px] text-zinc-600 sm:flex-row sm:items-center sm:px-6 lg:px-8">
            <p className="font-mono">
              ACES · started {data ? timeAgo(data.state.started_at) : "…"} · state synced to private
              GitHub backup
            </p>
            <p className="font-mono">
              <span className="hidden md:inline">
                shortcuts: <kbd className="rounded border border-zinc-700 bg-zinc-900 px-1 text-[9px]">1–7</kbd>{" "}
                tabs · <kbd className="rounded border border-zinc-700 bg-zinc-900 px-1 text-[9px]">r</kbd>{" "}
                refresh · <kbd className="rounded border border-zinc-700 bg-zinc-900 px-1 text-[9px]">p</kbd>{" "}
                print · <kbd className="rounded border border-zinc-700 bg-zinc-900 px-1 text-[9px]">⌘K</kbd>{" "}
                search ·{" "}
              </span>
              honest by design — verified earnings only · next autonomous wake ≤ 6h
            </p>
          </div>
        </footer>

        {/* scroll-to-top */}
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            type="button"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            aria-label="Scroll back to top"
            className="fixed bottom-6 right-6 z-40 flex size-10 items-center justify-center rounded-full border border-zinc-700/70 bg-zinc-900/90 text-zinc-300 shadow-lg backdrop-blur transition-colors hover:border-emerald-500/40 hover:text-emerald-300 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-500/60 motion-reduce:transition-none"
          >
            <ArrowUp className="size-4" aria-hidden="true" />
          </motion.button>
        )}

        {/* global search palette */}
        <CommandPalette
          open={paletteOpen}
          onOpenChange={setPaletteOpen}
          data={data}
          onNavigate={navigate}
          onRefresh={load}
        />

        {/* print-only truthful mission report — replaces the whole interactive
            dashboard on paper (Ctrl/Cmd+P, "p", palette action, header button) */}
        {data && <PrintReport data={data} />}
      </div>
    </MotionConfig>
  );
}
